import { createId } from './id';
import type {
  CaptionSettings,
  ExportSettings,
  MusicSettings,
  ReelProject,
  SharedDefaults,
  VoiceoverSettings,
  WatermarkSettings,
  Workspace,
} from '@/types';

export function defaultVoiceoverSettings(): VoiceoverSettings {
  return {
    overridesShared: false,
    script: '',
    provider: 'google',
    languageCode: 'en-US',
    voiceName: 'en-US-Neural2-F',
    voiceGender: 'FEMALE',
    openaiVoice: 'alloy',
    openaiModel: 'tts-1',
    speakingRate: 1,
    pitch: 0,
    volume: 1,
    silenceBefore: 0,
    silenceAfter: 0.3,
    startAt: 0,
    endAt: null,
    trimStart: 0,
    trimEnd: 0,
    generatedMediaId: null,
    generatedDurationSeconds: null,
    wordTimings: null,
    timingSource: null,
  };
}

export function defaultCaptionSettings(): CaptionSettings {
  return {
    overridesShared: false,
    enabled: true,
    preset: 'clean',
    fontFamily: 'Inter',
    fontSizePx: 64,
    fontWeight: 700,
    textColor: '#ffffff',
    outlineColor: '#000000',
    outlineWidthPx: 6,
    backgroundColor: '#000000',
    backgroundOpacity: 0,
    alignment: 'center',
    lineHeight: 1.25,
    letterSpacingPx: 0,
    maxCharsPerLine: 24,
    maxLines: 2,
    position: 'bottom',
    customRect: { x: 0.1, y: 0.72, width: 0.8, height: 0.18 },
    widthPercent: 84,
    safeMarginPercent: 8,
    casing: 'original',
    animation: 'pop',
    activeWordColor: '#4a5cf7',
  };
}

export function defaultMusicSettings(): MusicSettings {
  return {
    overridesShared: false,
    enabled: false,
    mediaId: null,
    volume: 0.35,
    startAt: 0,
    trimStart: 0,
    trimEnd: null,
    loop: true,
    fadeInSeconds: 1,
    fadeOutSeconds: 1.5,
    duckingEnabled: true,
    duckingAmount: 0.6,
  };
}

export function defaultWatermarkSettings(): WatermarkSettings {
  return {
    overridesShared: false,
    enabled: true,
    source: 'default',
    mediaId: null,
    widthPercent: 20,
    opacity: 0.85,
    positionPreset: 'bottom-right',
    offsetXPercent: 4,
    offsetYPercent: 4,
    alwaysVisible: true,
    startAt: 0,
    endAt: null,
  };
}

export function defaultExportSettings(): ExportSettings {
  return {
    overridesShared: false,
    widthPx: 1080,
    heightPx: 1920,
    frameRate: 30,
    quality: 'standard',
    matchDurationToVoiceover: false,
    fileNameMode: 'from-title',
    customFileName: '',
    fileNamePrefix: 'reel',
    fileNameSuffix: '',
    useSequentialNumbering: true,
  };
}

export function defaultSharedSettings(): SharedDefaults {
  return {
    voiceover: { ...defaultVoiceoverSettings(), overridesShared: true },
    captions: { ...defaultCaptionSettings(), overridesShared: true },
    music: { ...defaultMusicSettings(), overridesShared: true },
    watermark: { ...defaultWatermarkSettings(), overridesShared: true },
    export: { ...defaultExportSettings(), overridesShared: true },
  };
}

export function createReelProject(title: string): ReelProject {
  const now = Date.now();
  return {
    id: createId('reel'),
    title,
    createdAt: now,
    updatedAt: now,
    images: [],
    voiceover: defaultVoiceoverSettings(),
    captions: defaultCaptionSettings(),
    music: defaultMusicSettings(),
    watermark: defaultWatermarkSettings(),
    export: defaultExportSettings(),
  };
}

export function createEmptyWorkspace(): Workspace {
  return {
    id: createId('workspace'),
    shared: defaultSharedSettings(),
    projectOrder: [],
    projects: {},
    jobs: {},
    jobOrder: [],
    activeProjectId: null,
    updatedAt: Date.now(),
  };
}
